import React, { useState } from 'react';
import { XMarkIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline';

interface SuspendUserModalProps {
  user: {
    id: string;
    numeric_id?: number;
    name: string;
    email: string;
    role: string;
  };
  onClose: () => void;
  onConfirm: (data: {
    suspension_type: 'temporary' | 'permanent';
    reason: string;
    duration_hours?: number;
    notify_user: boolean;
  }) => Promise<void>;
}

const SuspendUserModal: React.FC<SuspendUserModalProps> = ({ user, onClose, onConfirm }) => {
  const [suspensionType, setSuspensionType] = useState<'temporary' | 'permanent'>('temporary');
  const [reason, setReason] = useState('');
  const [durationHours, setDurationHours] = useState('24');
  const [notifyUser, setNotifyUser] = useState(true);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!reason.trim()) {
      alert('Please provide a reason for suspension');
      return;
    }

    if (reason.trim().length < 10) {
      alert('Reason must be at least 10 characters');
      return;
    }

    setLoading(true);
    try {
      await onConfirm({
        suspension_type: suspensionType,
        reason: reason.trim(),
        duration_hours: suspensionType === 'temporary' ? parseInt(durationHours) : undefined,
        notify_user: notifyUser,
      });
    } finally {
      setLoading(false);
    }
  };

  const presetDurations = [
    { label: '1 Hour', hours: 1 },
    { label: '6 Hours', hours: 6 },
    { label: '24 Hours (1 Day)', hours: 24 },
    { label: '72 Hours (3 Days)', hours: 72 },
    { label: '168 Hours (1 Week)', hours: 168 },
    { label: '720 Hours (30 Days)', hours: 720 },
  ];

  const commonReasons = [
    'Violation of terms of service',
    'Suspicious account activity',
    'Multiple customer complaints',
    'Failed identity verification',
    'Fraudulent activity detected',
    'Inappropriate behavior',
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-volexx-lg shadow-volexx max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-red-50 border-b border-red-200 p-6 flex justify-between items-start">
          <div>
            <div className="flex items-center mb-2">
              <ShieldExclamationIcon className="h-8 w-8 text-red-600 mr-3" />
              <h2 className="text-2xl font-bold text-red-900 font-poppins">Suspend User Account</h2>
            </div>
            <p className="text-sm text-red-700 font-poppins">
              <span className="font-semibold">{user.name}</span> ({user.email})
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-red-600 hover:text-red-800 transition-colors"
            disabled={loading}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Suspension Type */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3 font-poppins">
              Suspension Type
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setSuspensionType('temporary')}
                className={`p-4 rounded-volexx border-2 transition-all ${
                  suspensionType === 'temporary'
                    ? 'border-volexx-blue-600 bg-volexx-blue-50 ring-2 ring-volexx-blue-200'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-semibold text-gray-900 font-poppins mb-1">Temporary</div>
                <div className="text-sm text-gray-600 font-poppins">Automatic unsuspend after duration</div>
              </button>
              <button
                type="button"
                onClick={() => setSuspensionType('permanent')}
                className={`p-4 rounded-volexx border-2 transition-all ${
                  suspensionType === 'permanent'
                    ? 'border-red-600 bg-red-50 ring-2 ring-red-200'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-semibold text-gray-900 font-poppins mb-1">Permanent</div>
                <div className="text-sm text-gray-600 font-poppins">Requires manual reinstatement</div>
              </button>
            </div>
          </div>

          {/* Duration (only for temporary) */}
          {suspensionType === 'temporary' && (
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-3 font-poppins">
                Suspension Duration
              </label>
              <div className="grid grid-cols-3 gap-3 mb-3">
                {presetDurations.map((preset) => (
                  <button
                    key={preset.hours}
                    type="button"
                    onClick={() => setDurationHours(preset.hours.toString())}
                    className={`px-3 py-2 rounded-volexx border text-sm font-poppins transition-all ${
                      parseInt(durationHours) === preset.hours
                        ? 'border-volexx-blue-600 bg-volexx-blue-50 text-volexx-blue-700 font-semibold'
                        : 'border-gray-300 hover:border-volexx-blue-300'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={durationHours}
                  onChange={(e) => setDurationHours(e.target.value)}
                  min="1"
                  max="8760"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent font-poppins"
                  placeholder="Custom hours"
                />
                <span className="text-sm text-gray-600 font-poppins">hours</span>
              </div>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3 font-poppins">
              Reason for Suspension *
            </label>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {commonReasons.map((commonReason) => (
                <button
                  key={commonReason}
                  type="button"
                  onClick={() => setReason(commonReason)}
                  className="px-3 py-2 text-left rounded-volexx border border-gray-300 hover:border-volexx-blue-300 text-sm font-poppins transition-colors"
                >
                  {commonReason}
                </button>
              ))}
            </div>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              required
              minLength={10}
              maxLength={500}
              className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-transparent font-poppins resize-none"
              placeholder="Provide a detailed reason for the suspension (10-500 characters)..."
            />
            <div className="flex justify-between mt-2">
              <p className="text-sm text-gray-500 font-poppins">
                This reason will be visible to the user
              </p>
              <p className="text-sm text-gray-500 font-poppins">
                {reason.length}/500
              </p>
            </div>
          </div>

          {/* Notification Option */}
          <div className="flex items-start">
            <input
              type="checkbox"
              id="notify-user"
              checked={notifyUser}
              onChange={(e) => setNotifyUser(e.target.checked)}
              className="mt-1 h-4 w-4 text-volexx-blue-600 border-gray-300 rounded focus:ring-volexx-blue-500"
            />
            <label htmlFor="notify-user" className="ml-3 text-sm font-poppins">
              <span className="font-semibold text-gray-900">Send email notification</span>
              <p className="text-gray-600 mt-1">
                User will receive an email explaining the suspension and how to appeal
              </p>
            </label>
          </div>

          {/* Warning Banner */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-volexx p-4">
            <p className="text-sm font-semibold text-yellow-900 font-poppins mb-2">
              ⚠️ Important
            </p>
            <ul className="text-sm text-yellow-800 font-poppins space-y-1 list-disc list-inside">
              <li>User will be immediately logged out from all sessions</li>
              <li>User cannot access their account until unsuspended</li>
              <li>All active orders will be affected (riders only)</li>
              <li>This action will be logged in the admin activity log</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-6 py-3 bg-gray-100 text-gray-700 rounded-volexx font-semibold font-poppins hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !reason.trim() || reason.trim().length < 10}
              className="px-6 py-3 bg-red-600 text-white rounded-volexx font-semibold font-poppins hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Suspending...
                </span>
              ) : (
                `Suspend ${suspensionType === 'temporary' ? 'Temporarily' : 'Permanently'}`
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SuspendUserModal;
